/**
 * Consent-gated analytics (GA4) and advertising measurement (Meta).
 *
 * GA4 used to load unconditionally from index.html — for every visitor, before any
 * consent, including signed-in students. Under GDPR, analytics cookies/identifiers
 * need affirmative consent, and for children they need a basis a school can't
 * casually hand over (Italy sets the digital-consent age at 14). That's a
 * due-diligence failure a DPO finds in minutes, so the tag now loads only here:
 *
 *   - STUDENTS ARE NEVER TRACKED. No banner, no tag, no decision to make.
 *   - EU/UK non-students (GDPR/PECR): explicit opt-IN via a banner; no yes = no tag.
 *   - Everyone else (e.g. the US): analytics is opt-OUT — default-on, disclosed in the
 *     privacy policy, with an opt-out in Settings. An explicit opt-out is honoured
 *     (and kills an already-running tag).
 *
 * Keep this the ONLY place either tag can be injected.
 */

import { disableMetaPixel, enableMetaPixel } from './metaPixel';

const GA_MEASUREMENT_ID = 'G-8TCC7JCPYY';
const CONSENT_KEY = 'luc_analytics_consent_v1';

export type ConsentChoice = 'granted' | 'denied';

/** The stored choice, or null if the visitor hasn't decided yet. */
export function getAnalyticsConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null; // storage blocked → treat as undecided, which means no tracking
  }
}

let injected = false;

/** Inject GA4. Idempotent, and never called without consent. */
function injectGa4(): void {
  if (injected || typeof document === 'undefined') return;
  injected = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag(...args: unknown[]) { w.dataLayer!.push(args); };
  w.gtag('js', new Date());
  /**
   * The role goes INSIDE config, not in a `set` afterwards.
   *
   * `config` is what fires the automatic page_view, and gtag's `set` only
   * applies to SUBSEQUENT events — so a set issued after this, however
   * immediately, lands behind the page_view already queued ahead of it. That
   * left every page_view role-less, which is precisely the hit a teacher-only
   * audience needs, and it is why this is a config parameter now.
   *
   * It also fixes the EU banner path: there, consent arrives long after the
   * role is known, so the tag is injected here with the role already stored and
   * no `set` would ever have been issued for that session at all.
   *
   * anonymize_ip is belt-and-braces: GA4 already truncates, but stating it
   * makes the DPA claim checkable rather than a promise.
   */
  w.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    ...(currentRole ? { user_properties: { user_role: currentRole } } : {}),
  });
}

/**
 * Record the visitor's choice and act on it. Granting clears the kill switch and
 * loads (or re-enables) the tag immediately; denying flips GA4's kill switch, which
 * stops an already-injected tag from sending anything more. Under the opt-out posture
 * a non-EU visitor's tag CAN be live before any choice, so denial genuinely has a
 * running tag to mute — hence disableGa4() rather than a no-op.
 */
export function setAnalyticsConsent(choice: ConsentChoice): void {
  try { localStorage.setItem(CONSENT_KEY, choice); } catch { /* storage blocked */ }
  if (choice === 'granted') {
    enableGa4(); injectGa4();
    enableMetaPixel();
  } else {
    // Explicit opt-out — stop already-injected tags from sending more. BOTH
    // tags move together on one consent decision; a second switch is how one
    // of them ends up still running after the teacher said no.
    disableGa4();
    disableMetaPixel();
  }
}

/**
 * Rough client-side "is this visitor in the EU/UK?" via their timezone. Not perfect,
 * but enough to decide POSTURE: EU/UK need explicit opt-IN (GDPR/PECR); elsewhere
 * (e.g. the US, our actual traffic) analytics is opt-OUT, so we default it on with a
 * privacy-policy disclosure + an opt-out, and skip the banner nag. Students are never
 * tracked regardless.
 */
function isLikelyEuVisitor(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    // Europe/* covers the mainland + UK. The extras are EEA/GDPR territories that
    // aren't under Europe/*: Iceland (Reykjavik), the Iberian Atlantic islands, and
    // the EU outermost regions (French overseas départements + Spain's Canaries).
    // Over-inclusion here is the SAFE direction (an extra opt-in banner), so we lean
    // generous; the opt-out still applies to anyone default-tracked.
    const EU_EXTRA = new Set([
      'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Reykjavik',
      'America/Martinique', 'America/Guadeloupe', 'America/Cayenne', 'America/Miquelon',
      'Indian/Reunion', 'Indian/Mayotte',
    ]);
    return tz.startsWith('Europe/') || EU_EXTRA.has(tz);
  } catch {
    return false; // can't tell → assume non-EU (default-on); the opt-out still applies
  }
}

/**
 * Clear GA4's kill switch so an already-injected tag can send again. Needed for
 * the opt-out → opt-in-again path within one session: `injected` stays true, so
 * injectGa4() no-ops, but the ga-disable flag set by a prior denial would keep the
 * live tag muted until a reload. Granting consent must undo that immediately.
 */
function enableGa4(): void {
  try {
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  } catch { /* no window (SSR/tests) */ }
}

/**
 * GA4's documented kill switch. Setting this stops an ALREADY-INJECTED tag from
 * sending anything more — which is the only way to honour "students are never
 * tracked" across a role change within one session.
 */
function disableGa4(): void {
  try {
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  } catch { /* no window (SSR/tests) */ }
}

/**
 * Call once the visitor's role is known. `isStudent` short-circuits everything:
 * students are never tracked and are never asked.
 *
 * Note the ordering: a signed-out visitor on a consented device legitimately loads
 * the tag, and a student may then sign in on that same device — so it isn't enough
 * to skip injection, we have to actively KILL a tag that's already running.
 */
export function initAnalytics(opts: { isStudent: boolean; role?: string | null }): void {
  // Students are never measured, in any region, under any consent — including
  // a teacher's granted consent on a shared device, which is why this kills
  // both tags rather than merely declining to inject them.
  if (opts.isStudent) { setUserRole(null); disableGa4(); disableMetaPixel(); return; }
  // BEFORE any injection below, so the page_view config carries it.
  setUserRole(analyticsRole(opts.role));
  const consent = getAnalyticsConsent();
  if (consent === 'denied') { disableGa4(); disableMetaPixel(); return; }  // explicit opt-out — honour it, and kill anything already running
  if (consent === 'granted') { enableGa4(); injectGa4(); enableMetaPixel(); return; }
  // Undecided: default-ON for non-EU visitors (US etc.) — opt-out model, disclosed in
  // the privacy policy with an opt-out in Settings. EU/UK visitors wait for the banner.
  if (!isLikelyEuVisitor()) { injectGa4(); enableMetaPixel(); }
}

/** Should we show the hard opt-in consent banner? Only EU/UK non-students who
 *  haven't decided — everyone else is default-on with an opt-out (no banner nag). */
export function shouldAskForAnalyticsConsent(opts: { isStudent: boolean }): boolean {
  if (opts.isStudent) return false;
  return getAnalyticsConsent() === null && isLikelyEuVisitor();
}

/**
 * Which kind of account is acting.
 *
 * 'student' is deliberately NOT a member. Students are never measured at all —
 * initAnalytics kills the tag for them — so a student value could only ever
 * arrive by mistake, and having the type permit it invites exactly that. Any
 * GA4 audience built on this is teacher-side by construction.
 */
export type UserRole = 'teacher' | 'board_admin' | 'district_admin';

let currentRole: UserRole | null = null;

/**
 * Narrow a profile's role string to something GA4 should see.
 *
 * A denylist would be the wrong shape here: a role added later would flow
 * straight into analytics unreviewed, and 'student' is precisely the value that
 * must never arrive. So this is an allowlist, and anything unrecognised becomes
 * null — role-less data, which is recoverable, rather than wrong data, which is
 * not.
 */
export function analyticsRole(role?: string | null): UserRole | null {
  // Exactly the non-student values UserProfile.role can hold (types.ts). An
  // earlier version also listed principal/vp/office_staff, which cannot occur:
  // school roles live on schoolAssociations and those users are 'teacher' at
  // the profile level. Listing them implied a granularity GA4 would never see,
  // so anyone building a "principals" audience would have got an empty one.
  const allowed: UserRole[] = ['teacher', 'board_admin', 'district_admin'];
  return allowed.find(r => r === role) || null;
}

/**
 * Attach the role to this visitor, for as long as the tag lives.
 *
 * BOTH mechanisms, because they answer different questions and GA4 does not
 * bridge them: the user PROPERTY is what audiences and user-scoped reports can
 * segment on, and the event PARAMETER is what shows up beside an individual
 * event in DebugView and in an exploration. Setting only the property leaves
 * every event looking role-less while you are trying to debug one.
 */
export function setUserRole(role: UserRole | null): void {
  currentRole = role;
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag !== 'function') return; // stored; injectGa4 will carry it
    /**
     * `null`, never `undefined`. This is the documented clear value for a user
     * property; gtag's merge behaviour for undefined is unspecified, and if it
     * is a no-op then a teacher's role stays attached to the next person on a
     * shared device — which is the one case this call exists to handle.
     */
    w.gtag('set', 'user_properties', { user_role: role ?? null });
  } catch { /* never let analytics break a real flow */ }
}

/**
 * Fire a GA4 custom event. No-op unless the GA4 tag is actually loaded (consent
 * granted, non-student) — so this inherits the same consent gate as everything
 * else and never tracks a student or an un-consented visitor. Used to make the
 * subscription funnel (paywall viewed → subscribe clicked → checkout canceled)
 * visible in GA4, complementing Stripe's own Checkout Sessions view.
 *
 * `user_role` rides along automatically. An explicit one in `params` wins, so a
 * caller that knows better than the stored role can say so — but nobody has to
 * remember to pass it, which is how a parameter ends up missing on the one
 * event somebody later wants to segment.
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag !== 'function') return;
    const withRole = currentRole ? { user_role: currentRole, ...(params || {}) } : (params || {});
    w.gtag('event', name, withRole);
  } catch { /* never let analytics break a real flow */ }
}
